// Utility functions for handling weight input with different units

/**
 * Parse weight input and extract number and unit
 * @param {string} weightInput - The weight input string (e.g., "100g", "2个", "1勺", "一大勺")
 * @returns {object} - Object with number and unit properties
 */
export const parseWeight = (weightInput) => {
    if (!weightInput || typeof weightInput !== 'string') {
        return { number: 0, unit: 'g', raw: '' };
    }

    const trimmed = weightInput.trim();
    if (!trimmed) {
        return { number: 0, unit: 'g', raw: '' };
    }

    // Chinese number map
    const chineseNumberMap = {
        '半': 0.5,
        '一': 1, '二': 2, '三': 3, '四': 4, '五': 5, '六': 6, '七': 7, '八': 8, '九': 9, '十': 10,
        '十一': 11, '十二': 12, '十三': 13, '十四': 14, '十五': 15, '十六': 16, '十七': 17, '十八': 18, '十九': 19, '二十': 20,
        '两': 2
    };

    // Supported Chinese unit characters
    const unitPattern = '勺|个|杯|片|块|条|根|瓣|粒|克|盒|袋|包|把|碗|滴|撮|丝|段|头|颗|只|支|张|层|圈|g|kg|ml|l|毫升|升';

    // Match Chinese number (optionally with 大) + unit
    const chineseRegex = new RegExp(`^(半|一|二|三|四|五|六|七|八|九|十一|十二|十三|十四|十五|十六|十七|十八|十九|二十|十|两)(大?)(${unitPattern})$`);
    const chineseNumberMatch = trimmed.match(chineseRegex);

    if (chineseNumberMatch) {
        const number = chineseNumberMap[chineseNumberMatch[1]] || 1;
        const isBig = chineseNumberMatch[2] === '大';
        const unit = chineseNumberMatch[3];
        const fullUnit = isBig ? `大${unit}` : unit;
        return { number, unit: fullUnit, raw: trimmed };
    }

    // Match regular number + unit pattern (e.g. "100g", "2个", "0.5杯")
    const match = trimmed.match(/^(\d+(?:\.\d+)?)(.*)$/);

    if (match) {
        const number = parseFloat(match[1]);
        const unit = match[2].trim();
        return { number, unit: unit || 'g', raw: trimmed };
    }

    // Fallback: could not parse the number, keep the raw text as-is
    return { number: 0, unit: '', raw: trimmed };
};

/**
 * Format weight for display
 * @param {number} number - The weight number
 * @param {string} unit - The weight unit
 * @param {string} raw - The raw input string
 * @returns {string} - Formatted weight string
 */
export const formatWeight = (number, unit, raw) => {
    if (number === 0 && raw) return raw;
    if (number === 0) return '';
    return `${number}${unit}`;
};

/**
 * Calculate adjusted weight based on servings
 * @param {number} originalWeight - Original weight number
 * @param {string} unit - Weight unit
 * @param {number} originalServings - Original recipe servings
 * @param {number} desiredServings - Desired servings
 * @returns {object} - Object with adjusted number and unit
 */
export const calculateAdjustedWeight = (originalWeight, unit, originalServings, desiredServings) => {
    const ratio = desiredServings / originalServings;
    const adjustedNumber = Math.round(originalWeight * ratio * 10) / 10;
    return { number: adjustedNumber, unit };
};
